import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Convert a relative API URL (e.g. "/api/media/abc123") into the
 * absolute, publicly-resolvable URL the backend serves it at. Used by
 * the "Publish to website" flow — the vendor downloads the image we
 * point them at, so the URL MUST be reachable from outside our app.
 */
export function toPublicUrl(maybeRelative) {
  if (!maybeRelative) return "";
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  const path = maybeRelative.startsWith("/") ? maybeRelative : `/${maybeRelative}`;
  return `${BACKEND_URL}${path}`;
}

/**
 * Force-convert a media URL to a vendor-acceptable JPEG URL.
 *
 * Lessons from the live tests against exoticindia.com:
 *   • The vendor's admin endpoint rejects PNG/WebP with "Only JPEG
 *     images are allowed".
 *   • It ALSO rejects URLs with query strings ("Invalid image format")
 *     — its parser splits on the last "." in the URL path looking for
 *     a recognised extension.
 *
 * So we produce ``…/api/media/<id>.jpg`` with NO query string. Our
 * backend's media route strips a trailing ``.jpg`` from the path and
 * implicitly transcodes to JPEG, so this URL Just Works.
 *
 * External CDN URLs (vendor's own images, Unsplash, etc.) pass through
 * untouched.
 */
export function toPublishableUrl(maybeRelative) {
  const abs = toPublicUrl(maybeRelative);
  if (!abs) return abs;
  if (!abs.includes("/api/media/")) return abs;
  const [path] = abs.split("?");           // drop any existing query
  if (/\.jpe?g$/i.test(path)) return path; // already a .jpg URL
  // Strip any other recognised image extension and replace with .jpg
  const cleaned = path.replace(/\.(webp|png)$/i, "");
  return `${cleaned}.jpg`;
}


// ---------------------------------------------------------------------
// Token storage (localStorage). We use httpOnly cookies AND a Bearer
// header so the app works even in browsers / contexts that block
// 3rd-party cookies — e.g. Emergent's embedded preview iframe, Safari,
// Brave, Chrome incognito with "Block third-party cookies" on.
//
// Backend supports both: `request.cookies.access_token` AND
// `Authorization: Bearer <token>` — see backend/auth.py::get_current_user.
// ---------------------------------------------------------------------
const ACCESS_TOKEN_KEY = "eia_access_token";
const REFRESH_TOKEN_KEY = "eia_refresh_token";

export function getAccessToken() {
  try { return localStorage.getItem(ACCESS_TOKEN_KEY) || ""; }
  catch { return ""; }
}
export function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_TOKEN_KEY) || ""; }
  catch { return ""; }
}
export function setAuthTokens({ access_token, refresh_token } = {}) {
  try {
    if (access_token) localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
    if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
  } catch { /* private mode — fall through to cookie path */ }
}
export function clearAuthTokens() {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch { /* ignore */ }
}

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Inject Bearer token on every outgoing request when we have one in
// localStorage. Cookies still ride along for browsers that allow them.
api.interceptors.request.use((config) => {
  const t = getAccessToken();
  if (t) {
    // axios 1.x uses AxiosHeaders — use `.set()` so the header survives
    // the AxiosHeaders proxy. Plain `config.headers.Authorization = ...`
    // silently drops on some 1.x builds.
    if (config.headers && typeof config.headers.set === "function") {
      if (!config.headers.get("Authorization")) {
        config.headers.set("Authorization", `Bearer ${t}`);
      }
    } else {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// ---------------------------------------------------------------------
// Auto-refresh interceptor — fixes the "page randomly logs me out"
// production bug. Without this, the moment the 12-hour access cookie
// expires the next API call returns 401 and ProtectedRoute bounces the
// user back to /login (even though their 14-day refresh cookie is
// still valid). Pattern: single-flight refresh promise so 5 concurrent
// 401s don't fire 5 /auth/refresh calls; replay each original request
// exactly once on success.
// ---------------------------------------------------------------------
let _refreshInflight = null;

async function _attemptRefresh() {
  if (!_refreshInflight) {
    const refreshToken = getRefreshToken();
    _refreshInflight = axios
      .post(
        `${API}/auth/refresh`,
        refreshToken ? { refresh_token: refreshToken } : {},
        { withCredentials: true },
      )
      .then((r) => {
        // Persist the rotated access token so subsequent requests use it
        // (in iframe contexts the Set-Cookie won't stick).
        if (r?.data?.access_token) {
          setAuthTokens({ access_token: r.data.access_token });
        }
        return r.data;
      })
      .catch((e) => { throw e; })
      .finally(() => {
        // Clear AFTER the chain resolves so duplicate 401s during the
        // same micro-window share the result.
        setTimeout(() => { _refreshInflight = null; }, 0);
      });
  }
  return _refreshInflight;
}

// Endpoints we MUST NOT auto-refresh on — otherwise we'd infinite-loop
// on the refresh endpoint itself, or silently re-login after a manual
// logout call.
const _NO_REFRESH_PATHS = ["/auth/refresh", "/auth/login", "/auth/logout"];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (status !== 401 || !original || original._retried) throw error;
    const url = original.url || "";
    if (_NO_REFRESH_PATHS.some((p) => url.endsWith(p))) throw error;
    original._retried = true;
    try {
      await _attemptRefresh();
    } catch {
      // Refresh failed — let the original 401 propagate so the app
      // surfaces a clean "session expired" state.
      clearAuthTokens();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        // Soft redirect: replace current url so back button doesn't
        // bounce them right back into the expired page.
        window.location.replace("/login");
      }
      throw error;
    }
    return api.request(original);
  },
);

// Channels surfaced in Campaign Studio — full multi-channel dashboard.
const ALLOWED_CAMPAIGN_CHANNELS = new Set([
  "email_single",
  "email_multi",
  "whatsapp",
  "rcs",
  "app_push",
  "web_push",
  "sms",
  "product_card",
]);

export async function fetchChannelDefaults() {
  const { data } = await api.get("/channel-defaults");
  return (data.channels || []).filter((c) => ALLOWED_CAMPAIGN_CHANNELS.has(c.id));
}

export async function fetchModels() {
  const { data } = await api.get("/models");
  return data;
}

// ---------------------------------------------------------------------
// Catalog variations — used by the Variations modal to surface color /
// size variants from the upstream catalog when a single itemcode maps
// to multiple rows (e.g. GAO082 returns 6 colors).
// ---------------------------------------------------------------------
export async function catalogVariations(itemcode) {
  const { data } = await api.get(`/catalog/variations/${encodeURIComponent(itemcode)}`, {
    timeout: 25000,
  });
  return data; // { itemcode, count, has_variations, variations: [...] }
}

// ---------------------------------------------------------------------
// Netcore analytics — campaign rollups + event feed + webhook health.
// ---------------------------------------------------------------------
export async function netcoreHealth() {
  const { data } = await api.get("/netcore/health", { timeout: 15000 });
  return data;
}

export async function netcoreCampaigns(days = 30) {
  const { data } = await api.get(`/netcore/campaigns?days=${days}`, { timeout: 30000 });
  return data; // { campaigns: [...], window_days }
}

export async function netcoreEvents({ limit = 100, campaign_id, event_type } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (campaign_id) params.set("campaign_id", campaign_id);
  if (event_type) params.set("event_type", event_type);
  const { data } = await api.get(`/netcore/events?${params.toString()}`, { timeout: 30000 });
  return data; // { events: [...], count }
}

export async function generateCopy(payload) {
  const { data } = await api.post("/campaigns/generate-copy", payload, { timeout: 180000 });
  return data.content;
}

export async function generateCopySingle(payload) {
  const { data } = await api.post("/campaigns/generate-copy-single", payload, { timeout: 120000 });
  return data.content;
}

export async function generateBanner(payload) {
  const { data } = await api.post("/campaigns/generate-banner", payload, { timeout: 180000 });
  return data.banner;
}

export async function tightenField(payload) {
  const { data } = await api.post("/campaigns/tighten", payload, { timeout: 60000 });
  return data.text;
}

export async function saveCampaign(payload) {
  const { data } = await api.post("/campaigns/save", payload);
  return data.campaign;
}

export async function listCampaigns() {
  const { data } = await api.get("/campaigns");
  return data;
}

export async function getCampaign(id) {
  const { data } = await api.get(`/campaigns/${id}`);
  return data;
}

export async function deleteCampaign(id) {
  const { data } = await api.delete(`/campaigns/${id}`);
  return data;
}

export async function cloneCampaign(id) {
  const { data } = await api.post(`/campaigns/${id}/clone`);
  return data.campaign;
}

export async function exportCampaignZip(payload, filename = "campaign.zip") {
  const res = await api.post("/campaigns/export-zip", payload, {
    responseType: "blob",
    timeout: 120000,
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function fetchProductByItemcode(itemcode) {
  const { data } = await api.get(`/products/by-itemcode/${encodeURIComponent(itemcode)}`, { timeout: 45000 });
  return data;
}

export async function listUsers() {
  const { data } = await api.get("/auth/users");
  return data.users;
}

export async function createUser(payload) {
  const { data } = await api.post("/auth/users", payload);
  return data.user;
}

export async function deleteUser(id) {
  const { data } = await api.delete(`/auth/users/${id}`);
  return data;
}

// ---- Netcore (Smartech) — direct push ----
export async function fetchNetcoreStatus() {
  const { data } = await api.get("/netcore/status");
  return data;
}

export async function fetchNetcoreSegments() {
  const { data } = await api.get("/netcore/segments");
  return data; // { segments: [...], mode: "mock"|"live" }
}

export async function pushEmailToNetcore(payload) {
  const { data } = await api.post("/netcore/push-email", payload, { timeout: 60000 });
  return data;
}

export async function pushChannelToNetcore(payload) {
  const { data } = await api.post("/netcore/push-channel", payload, { timeout: 60000 });
  return data;
}

export async function pushTemplateToNetcore(payload) {
  const { data } = await api.post("/netcore/push-template", payload, { timeout: 60000 });
  return data;
}

export async function pushBannerToNetcore(payload) {
  const { data } = await api.post("/netcore/push-banner", payload, { timeout: 60000 });
  return data;
}

export async function listNetcoreTemplates() {
  const { data } = await api.get("/netcore/templates");
  return data;
}

export async function fetchUsageStats(days = 30) {
  const { data } = await api.get(`/usage/stats?days=${days}`);
  return data;
}

export async function fetchUsageRecent(limit = 50) {
  const { data } = await api.get(`/usage/recent?limit=${limit}`);
  return data.items || [];
}

// ---- AMP for Email ----
export async function fetchAmpLibrary() {
  const { data } = await api.get("/amp/library");
  return data.library || [];
}

export async function buildAmpFromLibrary(payload) {
  const { data } = await api.post("/amp/build-from-library", payload);
  return data; // { amp_html, validation }
}

export async function uploadMedia(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
  return data; // { id, url, size, content_type }
}

export async function uploadMediaFromDataUri(dataUri) {
  const { data } = await api.post("/media/from-data-uri", { data_uri: dataUri });
  return data;
}

export async function generateAmpAi(payload) {
  const { data } = await api.post("/amp/generate", payload, { timeout: 120000 });
  return data; // { amp_html, validation }
}

export async function validateAmp(html) {
  const { data } = await api.post("/amp/validate", { html });
  return data; // { ok, errors, warnings }
}

export async function compileAmpBlocks(tree) {
  const { data } = await api.post("/amp/compile-blocks", { tree });
  return data; // { amp_html, validation }
}

export async function saveAmp(payload) {
  const { data } = await api.post("/amp/save", payload);
  return data.amp;
}

export async function listAmp() {
  const { data } = await api.get("/amp");
  return data.items || [];
}

export async function getAmp(id) {
  const { data } = await api.get(`/amp/${id}`);
  return data;
}

export async function deleteAmp(id) {
  const { data } = await api.delete(`/amp/${id}`);
  return data;
}

export async function pushAmpToNetcore(payload) {
  const { data } = await api.post("/amp/push-to-netcore", payload, { timeout: 60000 });
  return data;
}


// ---- Cutout Studio (Gemini Nano Banana) ----
export async function cutoutLimits() {
  const { data } = await api.get("/cutout/limits");
  return data;
}

// Job pattern: a multi-image batch can take several minutes (Nano Banana
// averages ~20s per call). Synchronous endpoints get killed by the K8s
// ingress around 120s — the browser surfaces that as "network error".
// `cutoutStart` returns a job_id quickly; `cutoutJob` is polled for
// progress and final URLs.
export async function cutoutStart(formData) {
  const { data } = await api.post("/cutout/start", formData, {
    timeout: 60000,  // start endpoint only does upload + GridFS write
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { job_id, total }
}

export async function cutoutJob(jobId) {
  const { data } = await api.get(`/cutout/job/${jobId}`, { timeout: 30000 });
  return data;
}

export async function processCutoutBatch(formData) {
  // Kept for legacy callers — internally now polls the job endpoint.
  const { job_id } = await cutoutStart(formData);
  // poll every 2.5s up to ~10 min
  const deadline = Date.now() + 10 * 60 * 1000;
  while (Date.now() < deadline) {
    const job = await cutoutJob(job_id);
    if (job.status === "done" || job.status === "error") return {
      items: job.items || [],
      reference_bg_url: job.reference_bg_url || null,
      reference_bg_id: job.reference_bg_id || null,
      style_prompt: job.style_prompt || null,
    };
    await new Promise((r) => setTimeout(r, 2500));
  }
  throw new Error("Cutout job timed out — check Cutout Studio for status.");
}

export async function zipMedia(media_ids, zip_name = "media.zip", opts = {}) {
  // opts: { format?: "jpg"|"jpeg"|"webp"|"png", quality?: number, max_kb?: number }
  const res = await api.post(
    "/cutout/zip",
    { media_ids, zip_name, ...opts },
    { responseType: "blob", timeout: 180000 },
  );
  return res.data;
}

// ---- Dimension Card workspace ----
export async function dimcardPreview(itemcode) {
  // 60s for the same reason mockupLookup uses 60s — the upstream catalog
  // API can take 10-20s on cold-cache hits, especially in bulk paste.
  const { data } = await api.get(`/dimcard/preview/${encodeURIComponent(itemcode)}`, { timeout: 60000 });
  return data;
}

export async function dimcardStart(payload) {
  const { data } = await api.post("/dimcard/start", payload, { timeout: 30000 });
  return data; // { job_id, total }
}

export async function dimcardJob(jobId) {
  const { data } = await api.get(`/dimcard/job/${jobId}`, { timeout: 30000 });
  return data;
}

export async function dimcardImport(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/dimcard/import", form, {
    timeout: 60000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { itemcodes, count, truncated }
}

// ----- Mockup Studio -----
export async function mockupLookup(itemcode) {
  // 60s — the upstream catalog API (exoticindia.com) can take 10-20s on
  // cold-cache hits, especially when the user pastes 20-30 codes that
  // all miss the catalog's CDN at once. The previous 15s timeout was
  // surfacing as the "15000 ms" error reported on production.
  const { data } = await api.get(`/mockup/lookup/${encodeURIComponent(itemcode)}`, { timeout: 60000 });
  return data; // { itemcode, title, image_url }
}

export async function mockupPreview(itemcode) {
  const { data } = await api.get(`/mockup/preview/${encodeURIComponent(itemcode)}`, { timeout: 60000 });
  return data;
}

export async function mockupStart(payload) {
  const { data } = await api.post("/mockup/start", payload, { timeout: 30000 });
  return data;
}

export async function mockupJob(jobId) {
  const { data } = await api.get(`/mockup/job/${jobId}`, { timeout: 30000 });
  return data;
}

export async function mockupRegenerate(payload) {
  const { data } = await api.post("/mockup/regenerate", payload, { timeout: 30000 });
  return data;
}

export async function mockupImport(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/mockup/import", form, {
    timeout: 60000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// Upload a single source image (JPG/PNG/WEBP, ≤8 MB) to be used in
// place of a catalog-fetched product image. Returns the GridFS id +
// URL the caller stores on its in-memory row.
export async function mockupUploadSource(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/mockup/upload-source", form, {
    timeout: 60000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { id, url, size, content_type }
}

// Upload a reference image (style/scene/mood cue). Same constraints as
// /upload-source (8 MB, JPG/PNG/WEBP).
export async function mockupUploadReference(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/mockup/upload-reference", form, {
    timeout: 60000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { id, url, size, content_type }
}

// Fetch an existing catalog product photo and stash it as a reference.
// Returns { id, url, size, itemcode, title } so the UI can show a chip.
export async function mockupReferenceFromItemcode(itemcode) {
  const { data } = await api.get(
    `/mockup/reference-from-itemcode/${encodeURIComponent(itemcode)}`,
    { timeout: 60000 },
  );
  return data;
}

// Bulk-import a ZIP of images + optional CSV/XLSX manifest. The backend
// returns one ready-to-use row per image, already uploaded to GridFS.
export async function mockupUploadZip(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/mockup/upload-zip", form, {
    timeout: 180000,    // 3 min — 100 MB / 200 images can take a moment
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { rows: [...], count, warnings }
}


// ----- Website Banner Studio -----
export async function bannerPreview(itemcode) {
  const { data } = await api.get(`/banner/preview/${encodeURIComponent(itemcode)}`, { timeout: 60000 });
  return data;
}

export async function bannerStart(payload) {
  const { data } = await api.post("/banner/start", payload, { timeout: 30000 });
  return data;
}

export async function bannerJob(jobId) {
  const { data } = await api.get(`/banner/job/${jobId}`, { timeout: 30000 });
  return data;
}

export async function bannerRegenerate(payload) {
  const { data } = await api.post("/banner/regenerate", payload, { timeout: 30000 });
  return data;
}


// ---------------------------------------------------------------------
// Per-studio session save / list / load / delete. ``studio`` is one of
// ``banner | mockup | dimcard``. The payload is opaque — the caller
// decides how to shape it (typically the studio's full row state).
// ---------------------------------------------------------------------
export async function listStudioSessions(studio) {
  const { data } = await api.get(`/sessions/${encodeURIComponent(studio)}`, { timeout: 30000 });
  return data;
}

export async function getStudioSession(studio, sessionId) {
  const { data } = await api.get(`/sessions/${encodeURIComponent(studio)}/${encodeURIComponent(sessionId)}`, { timeout: 30000 });
  return data;
}

export async function saveStudioSession(studio, payload) {
  // payload = { id?, name, theme?, payload }
  const { data } = await api.post(`/sessions/${encodeURIComponent(studio)}`, payload, { timeout: 30000 });
  return data.session;
}

export async function deleteStudioSession(studio, sessionId) {
  const { data } = await api.delete(`/sessions/${encodeURIComponent(studio)}/${encodeURIComponent(sessionId)}`, { timeout: 30000 });
  return data;
}

// ---------------------------------------------------------------------
// Dimension Card uploads — single image + bulk ZIP. Mirrors Mockup
// Studio's upload flow so the user can build dim cards from arbitrary
// product photos when the catalog API doesn't have them.
// ---------------------------------------------------------------------
export async function dimcardUploadSource(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/dimcard/upload-source`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return data;
}

export async function dimcardUploadZip(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/dimcard/upload-zip`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
}



// ---------------------------------------------------------------------
// Mockup Studio · scheduled batches.
// ---------------------------------------------------------------------
export async function mockupStartBatch(payload) {
  // payload = { items, bulk_mood?, name, scheduled_for? }
  const { data } = await api.post(`/mockup/start`, payload, { timeout: 60000 });
  return data;
}

export async function mockupListBatches() {
  const { data } = await api.get(`/mockup/batches`, { timeout: 30000 });
  return Array.isArray(data?.batches) ? data.batches : [];
}

export async function mockupCancelBatch(jobId) {
  const { data } = await api.delete(`/mockup/batches/${encodeURIComponent(jobId)}`, { timeout: 15000 });
  return data;
}

// Bulk-clear the current user's finished batches. Defaults to
// done/error/cancelled/partial — running + scheduled batches are
// always left in place by the backend allow-list.
export async function mockupClearBatches(statuses = ["done", "error", "cancelled", "partial"]) {
  const status = statuses.join(",");
  const { data } = await api.delete(`/mockup/batches`, { params: { status }, timeout: 30000 });
  return data;
}



// ---------------------------------------------------------------------
// Product Shoot Studio.
// ---------------------------------------------------------------------
export async function productShootUploadImages(files) {
  const form = new FormData();
  for (const f of files) form.append("files", f);
  const { data } = await api.post("/product-shoot/upload-images", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
}

export async function productShootUploadZip(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/product-shoot/upload-zip", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return data;
}

export async function productShootStart(payload) {
  // payload = { products: [{label, source_image_ids[], title?, category?, on_model?, model_type?}],
  //             name?, scheduled_for? }
  const { data } = await api.post("/product-shoot/start", payload, { timeout: 60000 });
  return data;
}

export async function productShootJob(jobId) {
  const { data } = await api.get(`/product-shoot/job/${jobId}`, { timeout: 30000 });
  return data;
}

export async function productShootListBatches() {
  const { data } = await api.get(`/product-shoot/batches`, { timeout: 30000 });
  return Array.isArray(data?.batches) ? data.batches : [];
}

export async function productShootCancelBatch(jobId) {
  const { data } = await api.delete(`/product-shoot/batches/${encodeURIComponent(jobId)}`, { timeout: 15000 });
  return data;
}

// ---------------------------------------------------------------------
// Amazon A+ Content Generator
// ---------------------------------------------------------------------
export async function aplusPreview(itemcode) {
  const { data } = await api.get(`/aplus/preview/${encodeURIComponent(itemcode)}`, { timeout: 15000 });
  return data; // { itemcode, title, category, image_url, material, dimensions_label, ... }
}

export async function aplusStart(items) {
  // items: [{ itemcode, title?, override_image_url? }]
  const { data } = await api.post(`/aplus/start`, { items }, { timeout: 30000 });
  return data; // { job_id, total }
}

export async function aplusGetJob(jobId) {
  const { data } = await api.get(`/aplus/job/${encodeURIComponent(jobId)}`, { timeout: 15000 });
  return data; // { job_id, status, progress, total, items: [...] }
}

// ---------------------------------------------------------------------
// Website Publish — push studio-generated images straight to the
// exoticindia.com product page via the vendor admin CRM endpoint.
// ---------------------------------------------------------------------
export async function websitePublishImageCount(itemcode, color = null) {
  const params = color ? { color } : {};
  const { data } = await api.get(
    `/website-publish/image-count/${encodeURIComponent(itemcode)}`,
    { params, timeout: 15000 },
  );
  return data; // { count, ok, error? }
}

export async function websitePublishSubmit(payload) {
  // payload: { itemcode, color?, items: [{position, image_url, source_kind?, source_id?, label?}] }
  const { data } = await api.post(`/website-publish/publish`, payload, { timeout: 45000 });
  return data; // { ok, status_code, message, response, history_id }
}

export async function websitePublishHistory({ itemcode, limit = 30 } = {}) {
  const params = { limit };
  if (itemcode) params.itemcode = itemcode;
  const { data } = await api.get(`/website-publish/history`, { params, timeout: 15000 });
  return data; // { items: [...] }
}

/**
 * Live one-shot diagnostic call against the vendor's product/fetch
 * endpoint. Returns the raw response (status, headers, body, parsed
 * payload) plus the images our extractor found. Use this from a debug
 * panel to understand "why does the dialog say 1 image when the
 * product page has 6?" — the answer is almost always that the vendor's
 * fetch endpoint really does only return 1 image, so the next step is
 * to ask the vendor team to extend the API.
 */
export async function websitePublishDebugFetch(itemcode) {
  const { data } = await api.get(
    `/website-publish/debug-fetch/${encodeURIComponent(itemcode)}`,
    { timeout: 20000 },
  );
  return data;
}

